import { z } from 'zod';
import type { SafeOutputConstraint } from '../../generated/api/types.gen.js';
import { zSafeOutputConstraint, zWorkflowStepInput } from '../../generated/api/zod.gen.js';

export type SafeOutputsMap = { [toolName: string]: SafeOutputConstraint };

/**
 * One constraint per row: the generated constraint schema, with the tool name added and the allowed values held as
 * the comma-separated text the person types (`undefined` until they type, so an untouched row saves no list).
 */
const entrySchema = zSafeOutputConstraint
  .omit({ allowedValues: true })
  .extend({ tool: z.string(), allowedValues: z.string().optional() });
// The step's `safeOutputs` map is edited as a list of rows, so the form's one field replaces it.
export const safeOutputsSchema = zWorkflowStepInput
  .pick({ safeOutputs: true })
  .extend({ entries: z.array(entrySchema) })
  .omit({ safeOutputs: true });
export type SafeOutputsValues = z.input<typeof safeOutputsSchema>;
type EntryValues = SafeOutputsValues['entries'][number];

export function toEntries(value?: SafeOutputsMap | null): EntryValues[] {
  return Object.entries(value ?? {}).map(([tool, c]) => ({
    tool,
    appliesTo: c.appliesTo,
    maxCalls: c.maxCalls,
    maxValue: c.maxValue,
    maxLength: c.maxLength,
    allowedValues: c.allowedValues === undefined ? undefined : c.allowedValues.join(', '),
  }));
}

/** The map the step stores: empty tool names are dropped, empty caps are left out. */
export function toMap(entries: EntryValues[]): SafeOutputsMap {
  const map: SafeOutputsMap = {};
  for (const { tool, allowedValues, ...caps } of entries) {
    const name = tool.trim();
    if (!name) continue;
    const constraint: SafeOutputConstraint = {};
    if (caps.appliesTo !== undefined) constraint.appliesTo = caps.appliesTo;
    if (caps.maxCalls !== undefined) constraint.maxCalls = caps.maxCalls;
    if (caps.maxValue !== undefined) constraint.maxValue = caps.maxValue;
    if (caps.maxLength !== undefined) constraint.maxLength = caps.maxLength;
    if (allowedValues !== undefined) {
      constraint.allowedValues = allowedValues
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    }
    map[name] = constraint;
  }
  return map;
}
